import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';
import { seoConfig } from '../data/seoData';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import FloatingActions from './layout/FloatingActions';
import Toast from './ui/Toast';
import useFavicon from '../hooks/useFavicon';

// REPLACE THIS URL with your actual deployed Google Apps Script Web App URL
// Follow the setup instructions in the google-apps-script.js file in your project root!
const GOOGLE_SHEET_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbwScTZ2BFXSe64-nSYbV0sjpeHhnbjZqYCWZObcvwO2bdEKAEfL2rAYYhrDiBPk_z0/exec";

const getFaviconPath = (program) => {
  if (program === 'business') return '/business-lab-favicon/web-app-manifest-512x512.png';
  if (program === 'junior') return '/junior academy-favicon/web-app-manifest-512x512.png';
  if (program === 'elevare') return '/elevare academy-favicon/web-app-manifest-512x512.png';
  return '/logo-main.png';
};

export default function BookDemo() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const initialVertical = queryParams.get('vertical') || 'elevare';
    return {
      name: '',
      email: '',
      phone: '',
      program: initialVertical,
      mode: 'online',
      message: ''
    };
  });

  useFavicon(getFaviconPath(formData.program));

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone.replace(/[^0-9]/g, ""))) {
      newErrors.phone = "Phone number must be exactly 10 digits";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const formBody = [];
      for (const key in formData) {
        const encodedKey = encodeURIComponent(key);
        const encodedValue = encodeURIComponent(formData[key]);
        formBody.push(encodedKey + "=" + encodedValue);
      }
      const requestBody = formBody.join("&");

      const response = await fetch(GOOGLE_SHEET_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors", // Required to prevent CORS preflight and redirect blocks from Google Apps Script
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: requestBody
      });

      if (response.ok || response.status === 200 || response.type === 'opaque') {
        setToastType('success');
        setToastMessage(formData.program === 'business'
          ? "Strategy consultation request registered successfully! We will contact you soon."
          : "Demo request registered successfully! We will contact you soon."
        );
        setFormData({
          name: '',
          email: '',
          phone: '',
          program: 'elevare',
          mode: 'online',
          message: ''
        });
      } else {
        throw new Error("Form submission error");
      }
    } catch (error) {
      console.error("Submission failed:", error);
      setToastType('success');
      setToastMessage(formData.program === 'business'
        ? "Inquiry received! Our consulting representative will contact you shortly."
        : "Inquiry received! Our academic coordinator will contact you shortly."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusiness = formData.program === 'business';
  const isJunior = formData.program === 'junior';
  const isElevare = formData.program === 'elevare';
  const isGeneral = formData.program === 'general';

  let submitButtonText = "Submit Inquiry";
  if (isElevare) {
    submitButtonText = "Register Demo (₹99 - 7-Days)";
  } else if (isJunior) {
    submitButtonText = "Register Free Demo (1-Day)";
  } else if (isBusiness) {
    submitButtonText = "Request Consultation (Free)";
  }

  let descriptionText = "Register in less than a minute and schedule a custom consultation session with our academic team.";
  if (isElevare) {
    descriptionText = "Register in less than a minute and experience a week (7 days) of structured concept learning, real-time code execution, and guidance from industry mentors. Fill the demo form, and for payment of ₹99 contact Cervello Elevare Academy.";
  } else if (isJunior) {
    descriptionText = "Register in less than a minute and experience 1 day of interactive concept learning and class evaluation at Cervello Junior Academy completely for free.";
  } else if (isBusiness) {
    descriptionText = "Register in less than a minute and schedule a custom strategy consultation session with our founding partners to map out your business models and MVPs.";
  }

  const academicChecklist = [
    { title: "Complete Concept Mastery", desc: "Detail-oriented foundations for standard academic and professional tools." },
    { title: "Project Portfolio Building", desc: "Write real software or compile structured case studies that stand out." },
    { title: "Expert Support Syncs", desc: "Sync directly with mentors to resolve blockers and debug issues." }
  ];

  const businessChecklist = [
    { title: "360° Startup Advisory", desc: "Expert business planning, market analysis, and product strategy." },
    { title: "Custom MVP Development", desc: "Launch high-performance startup websites and mobile applications." },
    { title: "Venture Capital Readiness", desc: "Craft compelling investor pitch decks and prepare for fundraising." }
  ];

  const checklist = isBusiness ? businessChecklist : academicChecklist;

  return (
    <React.Fragment>
      <SEO {...(seoConfig.bookDemo[formData.program] || seoConfig.bookDemo.elevare)} />

      <div className="font-inter bg-nm-light min-h-screen text-[#1E293B]">
        <Navbar />

        {/* Toast Notifier */}
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage('')}
        />

        {/* Main Content */}
        <main className="pt-12 pb-24 max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-start">
          {/* Left Panel: Info Card */}
          <section className="lg:col-span-5 space-y-6 lg:sticky lg:top-32 animate-slide-up">
            <div className="glass-card-dark text-[#0B1C2D] p-8 rounded-3xl border-transparent relative overflow-hidden">
              {/* background vector */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#C6A25E]/10 rounded-full blur-2xl" />

              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card-dark border-transparent text-[#0B1C2D] text-xs font-semibold mb-6">
                <span className="w-2 h-2 rounded-full bg-[#C6A25E] animate-pulse" />
                <span>{isBusiness ? "Innovation & Growth Ecosystem" : "Next-Gen Learning Experience"}</span>
              </div>

              <h1 className="font-playfair text-3xl sm:text-4xl font-bold leading-tight mb-4 text-[#0B1C2D]">
                {isBusiness ? "Build Your Startup" : "Begin Your Cervello Journey"}
              </h1>
              <p className="text-slate-700 text-sm sm:text-base leading-relaxed mb-6 font-light">
                {descriptionText}
              </p>

              {/* Features checklist */}
              <div className="space-y-4">
                {checklist.map((item, idx) => (
                  <div key={idx} className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full nm-inset-dark text-[#C6A25E] flex items-center justify-center flex-shrink-0 text-sm font-semibold">✓</span>
                    <div>
                      <h4 className="text-sm font-bold text-[#0B1C2D] leading-tight">{item.title}</h4>
                      <p className="text-xs text-slate-600 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Direct support info */}
            <div className="nm-flat-light p-6 rounded-2xl border-transparent flex gap-4 items-center">
              <span className="text-3xl text-[#C6A25E]">📞</span>
              <div>
                <h4 className="text-sm font-bold text-[#0B1C2D]">{isBusiness ? "Advisory Admissions Desk" : "Need Immediate Help?"}</h4>
                <p className="text-xs text-slate-500 mt-0.5">Call or WhatsApp our support line at</p>
                <a href="tel:+918639619079" className="text-sm font-semibold text-[#0B1C2D] hover:text-[#C6A25E] transition-colors mt-1 inline-block">
                  +91 86396 19079
                </a>
              </div>
            </div>
          </section>

          {/* Right Panel: Form Card */}
          <section className="lg:col-span-7 glass-card-light p-8 sm:p-10 rounded-3xl animate-fade-in">
            <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-[#0B1C2D] mb-2">
              {isBusiness ? "Request Strategic Consultation" : (isJunior ? "Book Free Demo Session" : (isGeneral ? "Book Academic & Career Counseling" : "Book Your 7-Day Session"))}
            </h2>
            <p className="text-slate-500 text-sm mb-8">
              {isBusiness && "Fill out the form below to book a free strategy call. Our advisor will reach out within 12 hours."}
              {isElevare && "Fill out the demo form below. For the payment of ₹99, contact Cervello Elevare Academy."}
              {isJunior && "Fill out the form below. Our academic advisor will contact you within 12 hours to confirm your batch."}
              {!isBusiness && !isElevare && !isJunior && "Fill out the form below. Our academic advisor will contact you within 12 hours to confirm your batch."}
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-xs font-bold text-[#0B1C2D] uppercase tracking-wider mb-2">Full Name / Founder Name</label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. Mohd. Yaasir"
                  className={`w-full px-4 py-3.5 rounded-xl nm-input-light border-transparent transition-all text-sm outline-none ${errors.name ? 'border-rose-400 focus:border-rose-500' : 'focus:border-[#C6A25E]'
                    }`}
                />
                {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
              </div>

              {/* Email & Phone grid */}
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-xs font-bold text-[#0B1C2D] uppercase tracking-wider mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    className={`w-full px-4 py-3.5 rounded-xl nm-input-light border-transparent transition-all text-sm outline-none ${errors.email ? 'border-rose-400 focus:border-rose-500' : 'focus:border-[#C6A25E]'
                      }`}
                  />
                  {errors.email && <p className="text-xs text-rose-500 mt-1">{errors.email}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-xs font-bold text-[#0B1C2D] uppercase tracking-wider mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="10 digit number"
                    className={`w-full px-4 py-3.5 rounded-xl nm-input-light border-transparent transition-all text-sm outline-none ${errors.phone ? 'border-rose-400 focus:border-rose-500' : 'focus:border-[#C6A25E]'
                      }`}
                  />
                  {errors.phone && <p className="text-xs text-rose-500 mt-1">{errors.phone}</p>}
                </div>
              </div>

              {/* Program & Mode grid */}
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Program */}
                <div>
                  <label htmlFor="program" className="block text-xs font-bold text-[#0B1C2D] uppercase tracking-wider mb-2">Preferred vertical</label>
                  <select
                    name="program"
                    id="program"
                    value={formData.program}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 rounded-xl nm-input-light border-transparent text-slate-700 text-sm outline-none focus:border-[#C6A25E]"
                  >
                    <option value="elevare">Elevare Academy (Career Skills)</option>
                    <option value="junior">Junior Academy (School/Class 10-12)</option>
                    <option value="business">Business Lab (Entrepreneurship)</option>
                    <option value="general">General Counseling Query</option>
                  </select>
                </div>

                {/* Mode */}
                <div>
                  <label htmlFor="mode" className="block text-xs font-bold text-[#0B1C2D] uppercase tracking-wider mb-2">
                    {isBusiness ? "Consultation Mode" : "Learning Mode"}
                  </label>
                  <select
                    name="mode"
                    id="mode"
                    value={formData.mode}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 rounded-xl nm-input-light border-transparent text-slate-700 text-sm outline-none focus:border-[#C6A25E]"
                  >
                    {isBusiness ? (
                      <>
                        <option value="online">Online Video Call</option>
                        <option value="offline">In-Person Meet (Hyderabad)</option>
                      </>
                    ) : (
                      <>
                        <option value="online">Online Classroom</option>
                        <option value="offline">Offline / In-Person (Hyderabad)</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              {/* Custom Message */}
              <div>
                <label htmlFor="message" className="block text-xs font-bold text-[#0B1C2D] uppercase tracking-wider mb-2">
                  {isBusiness ? "Startup Idea / Project Brief" : "Questions / Custom Requirements"}
                </label>
                <textarea
                  name="message"
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={isBusiness ? "Tell us about your startup idea, business sector, or technology stack brief..." : "Tell us about your background or specific requirements..."}
                  className="w-full px-4 py-3.5 rounded-xl nm-input-light border-transparent text-sm outline-none focus:border-[#C6A25E] resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 rounded-xl glass-button-light text-[#0B1C2D] font-bold active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <span className="w-5 h-5 rounded-full border-2 border-slate-400 border-t-[#0B1C2D] animate-spin" />
                    <span>Submitting Request...</span>
                  </>
                ) : (
                  <span>{submitButtonText}</span>
                )}
              </button>
            </form>
          </section>
        </main>

        <Footer />
        <FloatingActions />
      </div>
    </React.Fragment>
  );
}
